"use client";

import { byLocale } from "@/lib/locale";
import { useId, useMemo, useRef, useState } from "react";
import { MapPin, X } from "@phosphor-icons/react/dist/ssr";
import { DEFAULT_PROVINCE_SLUGS, REMOTE_SLUG, formatLocation, type LocationSlug, type Province, type ProvinceSlug, provinceLabel } from "@/lib/provinces";
import { provincesFromSlugs, searchLocations, type WardSuggestion } from "@/lib/province-search";
import { useLocale } from "@/lib/hooks/use-locale";
import { useTranslations } from "next-intl";
import type { Locale } from "@/lib/locale";

/**
 * Pick the right province label for the active locale. VN = Vietnamese
 * (`name`, with diacritics — "Hà Nội"); everything else falls back to the
 * English ASCII name (`nameEn` — "Ha Noi"). The dataset only carries the
 * two languages, so FR collapses to EN.
 */


// Inline province filter: search input + filtered radio list. Drop-in
// replacement for the previous flat 5–7 row radio list on the desktop
// search sidebar and the mobile composer drawer, but now reaches all 34
// post-2025-reform provinces via type-to-filter, plus ward-name shortcut
// suggestions ("Hoàn Kiếm" → Hà Nội).
//
// Two state semantics across the two callers, both supported via the
// `onChange` prop alone:
//
//   - Desktop sidebar (`SearchFilters`): live URL update on every pick.
//   - Mobile composer (`SearchComposer`): buffered — `onChange` writes to
//     local draft state, real navigation happens on "Show Results".
//
// Behaviour:
//
//   - Empty query → "All Locations" + the 7 popular provinces + "Remote"
//     (the existing radio list, unchanged). This is the default that 80%+
//     of readers stop at.
//   - Query → live filter across all 34 provinces by Vietnamese name,
//     English name, and slug. Diacritic-insensitive: "ha noi" matches
//     "Hà Nội".
//   - Query ≥ 2 chars → also surfaces ward suggestions ("Hoàn Kiếm → Hà
//     Nội") for provinces NOT already in the direct-match list. Capped at
//     6 — additional matches are silently dropped (user keeps typing
//     until their match surfaces).
//   - Empty result set → "No matching province" microcopy; user can clear
//     the query and start over.
//
// A11y: input is a real `<input type="search">`; the option list is a
// `role="radiogroup"` (not combobox, because we don't pop a portal — the
// list is always visible). Each row is a styled radio matching the
// existing `FilterRow` shape in `SearchFilters.tsx`.

interface Props {
  /** Current filter value — province slug, 'remote', or '' for All. */
  value: LocationSlug;
  /** Called when the user picks an option. */
  onChange: (next: LocationSlug) => void;
  /** Show the "All Locations" reset row at the top. Default true. */
  allowAll?: boolean;
  /** Show "Remote / Online" as a selectable option. Default true. */
  allowRemote?: boolean;
  /** Override which provinces appear as the empty-query default list. */
  defaultSlugs?: readonly ProvinceSlug[];
  /** Aria-label for the surrounding radiogroup. */
  groupLabel?: string;
  /** 44px min-height touch-target rows + tighter gap, to match the mobile
   *  search composer's category/language lists (which use min-h-[44px] +
   *  space-y-5). Default false keeps the compact desktop-sidebar rows. */
  touchTargets?: boolean;
}

export default function ProvinceFilter({
  value,
  onChange,
  allowAll = true,
  allowRemote = true,
  defaultSlugs = DEFAULT_PROVINCE_SLUGS,
  groupLabel = "Filter by province",
  touchTargets = false,
}: Props) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const trimmed = query.trim();
  const locale = useLocale();
  const t = useTranslations("search");

  // Stable references via `useMemo` — searchLocations walks 34 provinces +
  // up to 3,321 wards on every keystroke; cheap (<1ms) but cached anyway.
  const defaultProvinces = useMemo(
    () => provincesFromSlugs(defaultSlugs),
    [defaultSlugs],
  );
  const search = useMemo(() => searchLocations(trimmed), [trimmed]);

  const isSearching = trimmed.length > 0;
  // When the user types "remote" or "online" we want to surface Remote in
  // the result list even though it isn't in the dataset.
  const remoteMatchesQuery =
    /^(r|re|rem|remo|remot|remote|o|on|onl|onli|onlin|online)/i.test(trimmed);

  function handleSelect(next: LocationSlug) {
    onChange(next);
  }

  function clearQuery() {
    setQuery("");
    inputRef.current?.focus();
  }

  const showEmptyResultMessage =
    isSearching &&
    search.provinces.length === 0 &&
    search.wards.length === 0 &&
    !(allowRemote && remoteMatchesQuery);

  return (
    <div>
      {/* Search input — uses `type="search"` for the native UA clear
          button on platforms that render one (we draw our own X anyway,
          but the type is a useful semantic). */}
      <div className="relative mb-13">
        <MapPin
          aria-hidden="true"
          className="pointer-events-none absolute left-13 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-gray-500"
          weight="bold"
        />
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          inputMode="search"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("enterLocation")}
          aria-label={t("enterLocation")}
          className="w-full rounded-control border border-navy/20 bg-white py-8 pl-34 pr-34 text-sm text-navy placeholder:text-gray-500 focus:border-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        />
        {query && (
          <button
            type="button"
            onClick={clearQuery}
            aria-label={t("clearSearch")}
            className="absolute right-8 top-1/2 flex h-[20px] w-[20px] -translate-y-1/2 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-navy/5 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
          >
            <X aria-hidden="true" weight="bold" className="h-[11px] w-[11px]" />
          </button>
        )}
      </div>

      <ul role="radiogroup" aria-label={groupLabel} className={touchTargets ? "space-y-5 [&_label]:min-h-[44px]" : "space-y-8"}>
        {/* DEFAULT VIEW — chips for the popular provinces. */}
        {!isSearching && (
          <>
            {allowAll && (
              <Row
                value=""
                current={value}
                label={t("allLocations")}
                onSelect={handleSelect}
              />
            )}
            {defaultProvinces.map((p) => (
              <Row
                key={p.slug}
                value={p.slug}
                current={value}
                label={provinceLabel(p, locale)}
                onSelect={handleSelect}
              />
            ))}
            {allowRemote && (
              <Row
                value={REMOTE_SLUG}
                current={value}
                label={formatLocation(REMOTE_SLUG, locale)}
                onSelect={handleSelect}
              />
            )}
          </>
        )}

        {/* SEARCHING — province direct matches first, then ward shortcuts. */}
        {isSearching && (
          <>
            {search.provinces.map((p) => (
              <Row
                key={p.slug}
                value={p.slug}
                current={value}
                label={provinceLabel(p, locale)}
                // Show the "other-locale" name as a subtitle when it differs
                // — gives readers a parallel reference (VN viewer sees the
                // EN name underneath, and vice versa).
                subtitle={
                  p.nameEn !== p.name
                    ? provinceLabel(p, locale === "vi" ? "en" : "vi")
                    : undefined
                }
                onSelect={handleSelect}
              />
            ))}

            {search.wards.length > 0 && (
              <li
                aria-hidden="true"
                className="pt-8 pb-3 text-[10px] font-semibold uppercase tracking-widest text-gray-500"
              >
                {t("wards")}
              </li>
            )}
            {search.wards.map((s) => (
              <WardRow
                key={s.province.slug + s.wardName}
                suggestion={s}
                current={value}
                locale={locale}
                onSelect={handleSelect}
              />
            ))}
            {allowRemote && remoteMatchesQuery && (
              <Row
                value={REMOTE_SLUG}
                current={value}
                label={formatLocation(REMOTE_SLUG, locale)}
                onSelect={handleSelect}
              />
            )}

            {showEmptyResultMessage && (
              <li className="rounded-control bg-navy/3 px-13 py-8 text-sm leading-relaxed text-gray-600">
                {t("noMatchingProvince")}
              </li>
            )}
          </>
        )}

        {/* Currently-selected value sits OUTSIDE the visible default list
            (e.g. a less-popular province): show it pinned at the bottom
            of the default view so the user can see what's active without
            having to search for it. */}
        {!isSearching && value && (
          <SelectedOutsideDefaults
            value={value}
            defaultProvinces={defaultProvinces}
            locale={locale}
          />
        )}
      </ul>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────────

function Row({
  value,
  current,
  label,
  subtitle,
  onSelect,
}: {
  value: LocationSlug;
  current: LocationSlug;
  label: string;
  subtitle?: string;
  onSelect: (next: LocationSlug) => void;
}) {
  const active = current === value;
  return (
    <li>
      <label className="flex cursor-pointer select-none items-center gap-13 text-sm text-gray-600 hover:text-navy">
        <input
          type="radio"
          name="province-filter"
          value={value}
          checked={active}
          onChange={() => onSelect(value)}
          className="sr-only"
        />
        <span
          className={`flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border transition-colors ${
            active ? "border-navy" : "border-navy/25 bg-white"
          }`}
          aria-hidden="true"
        >
          {active && <span className="h-[7px] w-[7px] rounded-full bg-navy" />}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className={active ? "text-navy" : ""}>{label}</span>
          {subtitle && (
            <span className="text-[11px] text-gray-500">{subtitle}</span>
          )}
        </span>
      </label>
    </li>
  );
}

function WardRow({
  suggestion,
  current,
  locale,
  onSelect,
}: {
  suggestion: WardSuggestion;
  current: LocationSlug;
  locale: Locale;
  onSelect: (next: LocationSlug) => void;
}) {
  const { wardName, wardNameEn, province } = suggestion;
  const active = current === province.slug;
  // Pick the ward name in the active locale — wards carry both
  // Vietnamese (`wardName`) and English (`wardNameEn`) labels in the index.
  const wardLabel = byLocale(locale, { en: wardNameEn, vi: wardName });
  const provinceName = provinceLabel(province, locale);
  return (
    <li>
      <label className="flex cursor-pointer select-none items-center gap-13 text-sm text-gray-600 hover:text-navy">
        <input
          type="radio"
          name="province-filter"
          value={province.slug}
          checked={active}
          onChange={() => onSelect(province.slug)}
          className="sr-only"
        />
        <span
          className={`flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border transition-colors ${
            active ? "border-navy" : "border-navy/25 bg-white"
          }`}
          aria-hidden="true"
        >
          {active && <span className="h-[7px] w-[7px] rounded-full bg-navy" />}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className={active ? "text-navy" : ""}>{wardLabel}</span>
          <span className="text-[11px] text-gray-500">
            in {provinceName}
          </span>
        </span>
      </label>
    </li>
  );
}

/**
 * Pin the active selection at the bottom of the default view when it
 * isn't one of the popular chips — so a pro filtering by Bắc Ninh sees a
 * row reflecting their current state without having to re-search.
 */
function SelectedOutsideDefaults({
  value,
  defaultProvinces,
  locale,
}: {
  value: LocationSlug;
  defaultProvinces: readonly Province[];
  locale: Locale;
}) {
  if (value === REMOTE_SLUG) return null; // Remote already in the default view
  if (defaultProvinces.some((p) => p.slug === value)) return null;

  const label = formatLocation(value, locale);
  if (!label) return null;

  return (
    <>
      <li
        aria-hidden="true"
        className="pt-8 pb-3 text-[10px] font-semibold uppercase tracking-widest text-gray-500"
      >
        Selected
      </li>
      <Row
        value={value}
        current={value}
        label={label}
        onSelect={() => {}}
      />
    </>
  );
}
